import type { AcceptInviteRequest, ForgotPasswordRequest, LoginRequest, RegisterRequest, ResetPasswordRequest } from '../types/user';
import { backendApiClient } from './BackendApiClient';
import { reportServerError } from './ErrorTrackingService';

const BASE_URL = process.env.REACT_APP_BACKEND_URL ?? '';

// Expected rejections (wrong password, taken email, bad code) are handled before this; server faults are reported.
function unexpectedFailure(response: Response, message: string): Error {
    const error = new Error(message);
    reportServerError(error, { url: response.url, status: response.status });
    return error;
}

export class AuthService {
    async register(credentials: RegisterRequest): Promise<void> {
        const response = await fetch(`${BASE_URL}/auth/register`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(credentials),
        });

        if (response.status === 409) {
            throw new Error('Den här e-postadressen är redan registrerad.');
        }

        if (!response.ok) {
            throw unexpectedFailure(response, 'Registreringen misslyckades. Försök igen.');
        }
    }

    async login(credentials: LoginRequest): Promise<void> {
        const response = await fetch(`${BASE_URL}/auth/login`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(credentials),
            credentials: 'include',
        });

        if (response.status === 401) {
            throw new Error('Fel e-postadress eller lösenord.');
        }

        if (!response.ok) {
            throw unexpectedFailure(response, 'Inloggningen misslyckades. Försök igen.');
        }

        backendApiClient.markAuthenticated();
    }

    async logout(): Promise<void> {
        await backendApiClient.clearSession();
    }

    async forgotPassword(request: ForgotPasswordRequest): Promise<void> {
        const response = await fetch(`${BASE_URL}/auth/forgot-password`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(request),
        });

        if (response.status === 404) {
            throw new Error('Inget konto hittades för den e-postadressen.');
        }

        if (!response.ok) {
            throw unexpectedFailure(response, 'Något gick fel. Försök igen.');
        }
    }

    async acceptInvite(request: AcceptInviteRequest): Promise<void> {
        const response = await fetch(`${BASE_URL}/invites/accept`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(request),
        });

        if (response.status === 400) {
            throw new Error('Ogiltig eller utgången inbjudningslänk.');
        }

        if (!response.ok) {
            throw unexpectedFailure(response, 'Något gick fel. Försök igen.');
        }
    }

    async resetPassword(request: ResetPasswordRequest): Promise<void> {
        const response = await fetch(`${BASE_URL}/auth/reset-password`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(request),
        });

        if (response.status === 400) {
            throw new Error('Ogiltig eller utgången kod.');
        }

        if (response.status === 404) {
            throw new Error('Inget konto hittades för den e-postadressen.');
        }

        if (!response.ok) {
            throw unexpectedFailure(response, 'Något gick fel. Försök igen.');
        }
    }
}

export const authService = new AuthService();
